import { Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";

export default function NewInHero({ hero }) {
  return (
    <section className="relative isolate overflow-hidden rounded-[2rem] border border-black/5 bg-gradient-to-br from-neutral-50 via-white to-neutral-100 shadow-2xl shadow-black/5">
      <div className="absolute -left-24 top-20 size-72 rounded-full bg-red-500/5 blur-3xl" />
      <div className="absolute -right-20 bottom-0 size-80 rounded-full bg-blue-500/5 blur-3xl" />

      <div className="grid min-h-[620px] lg:grid-cols-[0.82fr_1.18fr]">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative z-20 flex items-end px-6 py-12 sm:px-10 sm:py-16 lg:items-center lg:px-14 xl:px-16"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-black uppercase tracking-[0.34em] text-muted-foreground">
              {hero.eyebrow}
            </p>

            <h1 className="mt-5 text-6xl font-black uppercase leading-[0.84] tracking-[-0.07em] text-neutral-950 sm:text-7xl md:text-8xl lg:text-[7rem] xl:text-[8.5rem]">
              {hero.title}
            </h1>

            <p className="mt-7 max-w-lg text-sm leading-7 text-muted-foreground sm:text-base">
              {hero.description}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href={hero.primaryAction.href}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-neutral-950 px-6 py-3 text-sm font-black uppercase text-white transition hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 sm:w-auto"
                >
                  {hero.primaryAction.label}
                  <ArrowRight className="size-4" />
                </Link>
              </motion.div>

              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href={hero.secondaryAction.href}
                  className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-neutral-950 bg-white/80 px-6 py-3 text-sm font-black uppercase text-neutral-950 backdrop-blur transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 sm:w-auto"
                >
                  {hero.secondaryAction.label}
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <div className="relative min-h-[420px] overflow-hidden lg:min-h-[620px]">
          <motion.img
            initial={{ opacity: 0, scale: 1.06, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            src={hero.image}
            alt={hero.imageAlt}
            className="absolute inset-0 size-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/15 to-transparent lg:from-white/80 lg:via-transparent" />

          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute right-6 top-8 size-24 rounded-[2rem] border border-white/30 bg-white/10 backdrop-blur-xl sm:right-10 sm:top-12"
          />
        </div>
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-12%] top-[24%] z-10 h-8 w-[80%] -rotate-[17deg] bg-red-500 sm:h-10 lg:left-[-8%] lg:top-[34%] lg:w-[67%]"
      />
    </section>
  );
}